import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { onSnapshot, doc } from 'firebase/firestore';
import { functions, db } from './firebase';
import './AutoUploadClaimPage.css';

const AutoUploadClaimPage = ({ user }) => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [claimStatus, setClaimStatus] = useState('idle');
    const [requestId, setRequestId] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [generationStatus, setGenerationStatus] = useState(null);
    const hasClaimedRef = useRef(false);

    useEffect(() => {
        if (!user || !token || hasClaimedRef.current) return;
        hasClaimedRef.current = true;
        setClaimStatus('claiming');

        const run = async () => {
            try {
                const claimFunc = httpsCallable(functions, 'claimAutoUpload');
                const result = await claimFunc({ token });
                const id = result.data?.requestId;
                if (!id) {
                    setClaimStatus('error');
                    setErrorMessage('Unexpected response from server.');
                    return;
                }
                setRequestId(id);
                setClaimStatus('generating');
            } catch (err) {
                console.error('Auto-upload claim failed:', err);
                setClaimStatus('error');
                if (err.code === 'functions/resource-exhausted') {
                    setErrorMessage('Daily limit reached. Please try again tomorrow.');
                } else if (err.code === 'functions/deadline-exceeded') {
                    setErrorMessage('This claim link has expired.');
                } else if (err.code === 'functions/already-exists') {
                    setErrorMessage('This map has already been claimed.');
                } else if (err.code === 'functions/permission-denied') {
                    setErrorMessage('This link belongs to a different Mythic Maps account.');
                } else if (err.code === 'functions/not-found') {
                    setErrorMessage('This claim link is no longer valid.');
                } else {
                    setErrorMessage(err.message || 'Something went wrong.');
                }
            }
        };

        run();
    }, [user, token]);

    useEffect(() => {
        if (!requestId) return;
        const unsub = onSnapshot(doc(db, 'GenerationRequests', requestId), (snap) => {
            const data = snap.data();
            if (!data) return;
            setGenerationStatus(data.status);
            if (data.status === 'openai_processing_complete') {
                setClaimStatus('uploading');
            }
            if (data.status === 'openai_processing_failed') {
                setClaimStatus('error');
                setErrorMessage(data.error || 'Generation failed. Please try again from the app.');
            }
        });
        return () => unsub();
    }, [requestId]);

    if (!user) {
        return (
            <div className="auto-upload-claim-page">
                <div className="auto-upload-claim-card">
                    <h2>Sign in to claim your Mythic map</h2>
                    <p>
                        {"We saved a spot to generate and upload your map. Sign in to your Mythic Maps account to continue."}
                    </p>
                    <Link to={`/?redirect=/auto-upload/${token}`} className="auto-upload-claim-primary">
                        Sign in
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="auto-upload-claim-page">
            <div className="auto-upload-claim-card">
                <h2>Adding your Mythic map to Strava</h2>

                {claimStatus === 'claiming' && (
                    <p>Confirming your claim...</p>
                )}

                {claimStatus === 'generating' && (
                    <>
                        <p>Generating your map. This usually takes under a minute.</p>
                        {generationStatus && <p className="auto-upload-claim-meta">Status: {generationStatus}</p>}
                    </>
                )}

                {claimStatus === 'uploading' && (
                    <>
                        <p>Map ready. Uploading to your Strava activity now...</p>
                        <button
                            type="button"
                            className="auto-upload-claim-primary"
                            onClick={() => navigate(`/map/${requestId}`)}
                        >
                            View the map
                        </button>
                    </>
                )}

                {claimStatus === 'error' && (
                    <>
                        <p className="auto-upload-claim-error">{errorMessage}</p>
                        <Link to="/settings#auto-upload" className="auto-upload-claim-secondary">
                            Manage auto-upload settings
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
};

export default AutoUploadClaimPage;
